import * as Tabs from '@radix-ui/react-tabs'
import { useState } from 'react'
import { match, P } from 'ts-pattern'
import { z } from 'zod'
import type { DismissedLead } from '../../../utils/api-client/api-schemas.ts'
import {
  type BeaconRunState,
  type CandidateState,
  candidateTone,
  currentStage,
  DETECTION_LENSES,
  entryPointCandidates,
  groupCandidates,
  type GroupNode,
  groupState,
  keptIn,
  keptTotal,
  lensLabel,
  type NodeState,
  operationState,
  pillFromState,
  STAGES,
  suspectedIn,
} from '../beacon-run.ts'
import * as styles from './beacon-live-view.css.ts'

export const SurfaceVariantSchema = z.enum(['zones', 'paths', 'radar'])
export type SurfaceVariant = z.infer<typeof SurfaceVariantSchema>

const VARIANT_LABELS: Record<SurfaceVariant, string> = {
  zones: 'Trust zones',
  paths: 'Attack paths',
  radar: 'Lens radar',
}

const truncate = (text: string, length: number) => (text.length > length ? `${text.slice(0, length - 1)}…` : text)

const LEGEND: readonly { state: NodeState | 'refuted'; label: string }[] = [
  { state: 'mapped', label: 'Mapped' },
  { state: 'investigating', label: 'Under investigation' },
  { state: 'examined', label: 'Examined' },
  { state: 'candidate', label: 'Suspected' },
  { state: 'finding', label: 'Verified finding' },
  { state: 'refuted', label: 'Refuted / dropped' },
]

export type BeaconSurfaceProps = {
  run: BeaconRunState
  /** A finished run's dismissed leads by finding id, so the inspector can say why one was dropped. */
  dismissedById?: ReadonlyMap<string, DismissedLead>
  initialVariant?: SurfaceVariant
}

/**
 * The attack surface as Beacon works through it, in three readings of the same state: trust zones
 * (entry points with their operations), attack paths (entry point → operation group → sensitive asset)
 * and the lens radar (the six detection lenses and what each has raised).
 */
export const BeaconSurface: React.FC<BeaconSurfaceProps> = ({ run, dismissedById, initialVariant = 'zones' }) => {
  const [variant, setVariant] = useState<SurfaceVariant>(initialVariant)
  const [focusKey, setFocusKey] = useState<string>()

  const inspect = (event: React.SyntheticEvent) => {
    const key = event.target instanceof Element ? event.target.closest('[data-key]')?.getAttribute('data-key') : null
    if (key) {
      setFocusKey(key)
    }
  }

  const detail = focusKey ? describeFocus({ run, key: focusKey, dismissedById }) : undefined

  return (
    <section className={styles.panel} aria-label="Attack surface">
      <Tabs.Root value={variant} onValueChange={value => setVariant(SurfaceVariantSchema.parse(value))}>
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>Attack surface</h2>
          <Tabs.List className={styles.tabs} aria-label="Visualization">
            {SurfaceVariantSchema.options.map(option => (
              <Tabs.Trigger key={option} value={option} className={styles.tab}>
                {VARIANT_LABELS[option]}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </div>
        <div className={styles.viz} onMouseOver={inspect} onFocus={inspect}>
          <Tabs.Content value="zones" tabIndex={-1}>
            <TrustZones run={run} />
          </Tabs.Content>
          <Tabs.Content value="paths" tabIndex={-1}>
            <AttackPaths run={run} />
          </Tabs.Content>
          <Tabs.Content value="radar" tabIndex={-1}>
            <LensRadar run={run} />
          </Tabs.Content>
        </div>
      </Tabs.Root>
      <div className={styles.legend} aria-hidden>
        {LEGEND.map(({ state, label }) => (
          <span key={state} className={styles.legendItem}>
            <i className={styles.dot({ state, isStill: true })} />
            {label}
          </span>
        ))}
      </div>
      <div className={styles.inspector} aria-live="polite">
        {detail ?? 'Hover or focus any zone, operation, control or blip for details.'}
      </div>
    </section>
  )
}

const ThreatModelPlaceholder: React.FC<{ run: BeaconRunState }> = ({ run }) =>
  run.status === 'failed' || run.status === 'cancelled' ? (
    <p className={styles.loading}>The run ended before Beacon built its threat model.</p>
  ) : (
    <>
      <div className={styles.skeleton} style={{ height: '240px' }} />
      <p className={styles.loading}>Building the threat model…</p>
    </>
  )

const Badges: React.FC<{ suspected: number; kept: number }> = ({ suspected, kept }) =>
  suspected || kept ? (
    <div className={styles.badges}>
      {suspected > 0 && (
        <span className={styles.badge({ tone: 'candidate' })} title={`${suspected} suspected`}>
          {suspected}
        </span>
      )}
      {kept > 0 && (
        <span className={styles.badge({ tone: 'finding' })} title={`${kept} verified findings`}>
          {kept}
        </span>
      )}
    </div>
  ) : null

// #region Trust zones

const zoneSpan = (mapping: string, operations: number) =>
  mapping === 'pending' || mapping === 'mapping'
    ? 4
    : Math.max(4, Math.min(12, Math.round(Math.sqrt(operations) * 0.9)))

export const TrustZones: React.FC<{ run: BeaconRunState }> = ({ run }) => {
  if (run.entryPointOrder.length === 0) {
    return <ThreatModelPlaceholder run={run} />
  }
  return (
    <div className={styles.mapWrap}>
      <div className={styles.zones}>
        {run.entryPointOrder.flatMap(id => {
          const ep = run.entryPoints[id]
          return ep ? [<Zone key={id} run={run} entryPointId={id} />] : []
        })}
      </div>
      <ZoneSidebar run={run} />
    </div>
  )
}

const Zone: React.FC<{ run: BeaconRunState; entryPointId: string }> = ({ run, entryPointId }) => {
  const ep = run.entryPoints[entryPointId]
  if (!ep) {
    return null
  }
  const operations = ep.opIds.length
  const candidates = entryPointCandidates(run, ep)
  const kept = keptIn(run, candidates)
  const meta = ep.mapping === 'pending' ? 'waiting' : ep.mapping === 'mapping' ? 'mapping…' : `${operations} ops`
  return (
    <section
      className={styles.zone({ mapping: ep.mapping, isActive: ep.active > 0 })}
      style={{ gridColumn: `span ${zoneSpan(ep.mapping, operations)}` }}
      data-key={`ep:${ep.id}`}
      tabIndex={0}
      aria-label={`${ep.name}: ${meta}, ${kept} findings`}
    >
      <header className={styles.zoneHeader}>
        <span className={styles.zoneName}>{ep.name}</span>
        <span className={styles.zoneMeta}>
          {meta} · {ep.kind.replace('_', ' ')}
        </span>
      </header>
      {ep.mapping === 'pending' || ep.mapping === 'mapping' ? (
        <div className={styles.skeleton} style={{ opacity: ep.mapping === 'pending' ? 0.5 : 1 }} />
      ) : ep.mapping === 'empty' ? (
        <p className={styles.zoneEmpty}>No operations a caller can invoke — not examined by any lens.</p>
      ) : (
        <div className={styles.groups}>
          {ep.groupKeys.flatMap(key => {
            const group = run.groups[key]
            if (!group) {
              return []
            }
            const state = groupState(run, group)
            const tone = state === 'finding' || state === 'candidate' ? state : 'none'
            return [
              <div key={key} className={styles.group({ tone })} data-key={`grp:${key}`} title={group.name}>
                {group.opIds.flatMap(operationId => {
                  const operation = run.operations[operationId]
                  return operation
                    ? [
                        <i
                          key={operationId}
                          className={styles.dot({ state: operationState(run, operation), isStill: false })}
                          data-key={`op:${operationId}`}
                        />,
                      ]
                    : []
                })}
              </div>,
            ]
          })}
        </div>
      )}
      <Badges suspected={suspectedIn(run, candidates)} kept={kept} />
    </section>
  )
}

const ZoneSidebar: React.FC<{ run: BeaconRunState }> = ({ run }) => {
  const repoKept = keptIn(run, run.repo.candidates)
  return (
    <aside className={styles.side}>
      <div>
        <h3 className={styles.sideHeading}>Sensitive assets</h3>
        <ul className={styles.sideList}>
          {run.assetOrder.flatMap(id => {
            const asset = run.assets[id]
            if (!asset) {
              return []
            }
            const operations = asset.opIds.flatMap(opId => run.operations[opId] ?? [])
            const examined = operations.filter(operation => operation.isExamined).length
            const kept = keptIn(
              run,
              operations.flatMap(operation => operation.candidates)
            )
            return [
              <li
                key={id}
                className={styles.asset}
                data-key={`asset:${id}`}
                tabIndex={0}
                aria-label={`${asset.name}: ${examined} of ${operations.length} reaching operations examined`}
              >
                <div className={styles.assetLine}>
                  <span className={styles.assetName}>{asset.name}</span>
                  <span className={styles.assetCount}>
                    {operations.length ? `${examined}/${operations.length}` : '—'}
                    {kept > 0 && <span className={styles.findingCount}> · {kept}</span>}
                  </span>
                </div>
                <div className={styles.assetTrack}>
                  <div
                    className={styles.assetFill}
                    style={{ width: `${operations.length ? (examined / operations.length) * 100 : 0}%` }}
                  />
                </div>
              </li>,
            ]
          })}
        </ul>
      </div>
      <div>
        <h3 className={styles.sideHeading}>Security controls</h3>
        <ul className={styles.sideList}>
          {run.controlOrder.flatMap(id => {
            const control = run.controls[id]
            if (!control) {
              return []
            }
            const kept = keptIn(run, control.candidates)
            return [
              <li
                key={id}
                className={styles.control}
                data-key={`ctl:${id}`}
                tabIndex={0}
                aria-label={`${control.name}: ${control.state}`}
              >
                <span className={styles.controlName}>{control.name}</span>
                {kept > 0 && <span className={styles.badge({ tone: 'finding' })}>{kept}</span>}
                <span
                  className={styles.controlState({
                    state: control.state === 'auditing' || control.state === 'audited' ? control.state : 'quiet',
                  })}
                >
                  {control.state}
                </span>
              </li>,
            ]
          })}
        </ul>
      </div>
      <div>
        <h3 className={styles.sideHeading}>Sinks</h3>
        {run.sinks ? (
          run.sinks.error ? (
            <div className={styles.callout({ tone: 'warn' })}>
              <b>Sink inventory did not run</b>
              <span>
                {run.sinks.error.replace('opengrep error: ', '')}. The sink-backward lens had nothing to trace.
              </span>
            </div>
          ) : (
            <div className={styles.callout({ tone: 'neutral' })}>{run.sinks.count} sinks inventoried.</div>
          )
        ) : (
          <div className={styles.callout({ tone: 'neutral' })}>Sinks are inventoried after the surface map.</div>
        )}
      </div>
      <div>
        <h3 className={styles.sideHeading}>Repository</h3>
        <div className={styles.control} data-key="repo" tabIndex={0}>
          <span className={styles.controlName}>Repository-wide hygiene</span>
          {repoKept > 0 && <span className={styles.badge({ tone: 'finding' })}>{repoKept}</span>}
          <span
            className={styles.controlState({
              state: run.repo.state === 'running' ? 'auditing' : run.repo.state === 'done' ? 'audited' : 'quiet',
            })}
          >
            {run.repo.state === 'done' ? 'examined' : run.repo.state}
          </span>
        </div>
      </div>
    </aside>
  )
}

// #endregion

// #region Attack paths

const ROW_HEIGHT = 17
const TOP = 34
const X_ENTRY = 180
const X_GROUP = 300
const X_OUT = 560
const X_ASSET = 700

/** A group of one operation is labelled by its handler: the mapper's name for such a group is often just a path fragment. */
const groupLabel = (run: BeaconRunState, group: GroupNode) => {
  const only = group.opIds.length === 1 ? run.operations[group.opIds[0] ?? '']?.handler : undefined
  return only || group.name
}

const curve = (x1: number, y1: number, x2: number, y2: number) =>
  `M${x1},${y1} C${(x1 + x2) / 2},${y1} ${(x1 + x2) / 2},${y2} ${x2},${y2}`

/** Positions for the attack-path columns: one row per operation group, entry points centred on theirs. */
const pathLayout = (run: BeaconRunState) => {
  const groups = run.entryPointOrder.flatMap(id => run.entryPoints[id]?.groupKeys ?? [])
  const bare = run.entryPointOrder.filter(id => (run.entryPoints[id]?.groupKeys.length ?? 0) === 0)
  const rows = Math.max(groups.length + bare.length, 16)
  const groupY = new Map(groups.map((key, index) => [key, TOP + index * ROW_HEIGHT + ROW_HEIGHT / 2]))
  const entryY = new Map(
    run.entryPointOrder.map((id, index) => {
      const keys = run.entryPoints[id]?.groupKeys ?? []
      const first = keys[0]
      const last = keys.at(-1)
      const y = match({ first, last, hasGroups: groups.length > 0 })
        .with(
          { first: P.string.minLength(1), last: P.string.minLength(1) },
          ({ first, last }) => ((groupY.get(first) ?? 0) + (groupY.get(last) ?? 0)) / 2
        )
        .with({ hasGroups: true }, () => TOP + (groups.length + bare.indexOf(id) + 0.5) * ROW_HEIGHT)
        .with({ hasGroups: false }, () => TOP + (index + 0.5) * ((rows * ROW_HEIGHT) / run.entryPointOrder.length))
        .exhaustive()
      return [id, y]
    })
  )
  const assetY = new Map(
    run.assetOrder.map((id, index) => [id, TOP + (index + 0.5) * ((rows * ROW_HEIGHT) / run.assetOrder.length)])
  )
  return { groups, rows, groupY, entryY, assetY, height: TOP + rows * ROW_HEIGHT + 10 }
}

export const AttackPaths: React.FC<{ run: BeaconRunState }> = ({ run }) => {
  if (run.entryPointOrder.length === 0) {
    return <ThreatModelPlaceholder run={run} />
  }
  const { groups, groupY, entryY, assetY, height } = pathLayout(run)
  const probed = new Set(groups.filter(key => run.groups[key]?.opIds.some(id => (run.operations[id]?.active ?? 0) > 0)))
  const hotAssets = new Set([...probed].flatMap(key => run.groups[key]?.assetIds ?? []))
  const links = groups.flatMap(key => {
    const group = run.groups[key]
    if (!group) {
      return []
    }
    const isProbed = probed.has(key)
    const isEntryActive = isProbed || (run.entryPoints[group.entryPoint]?.active ?? 0) > 0
    const y = groupY.get(key) ?? 0
    const reachesAsset = group.assetIds.some(asset => assetY.has(asset))
    return [
      {
        id: `${key}>`,
        isActive: isEntryActive,
        d: curve(X_ENTRY + 6, entryY.get(group.entryPoint) ?? 0, X_GROUP - 5, y),
      },
      ...(reachesAsset ? [{ id: `${key}-`, isActive: isProbed, d: `M${X_GROUP + 5},${y} H${X_OUT}` }] : []),
      ...group.assetIds.flatMap(asset =>
        assetY.has(asset)
          ? [
              {
                id: `${key}>${asset}`,
                isActive: isProbed,
                d: curve(X_OUT, groupY.get(key) ?? 0, X_ASSET - 7, assetY.get(asset) ?? 0),
              },
            ]
          : []
      ),
    ]
  })
  // Active links draw last so they sit on top of the idle ones.
  const ordered = [...links.filter(each => !each.isActive), ...links.filter(each => each.isActive)]

  return (
    <div className={styles.pathWrap}>
      <div className={styles.svgBox}>
        <svg
          viewBox={`0 0 880 ${height}`}
          className={styles.pathSvg}
          role="img"
          aria-label="Entry points to operation groups to sensitive assets"
        >
          <text className={styles.svgColumnHead} x={X_ENTRY} y={16} textAnchor="end">
            ENTRY POINTS
          </text>
          <text className={styles.svgColumnHead} x={X_GROUP - 5} y={16}>
            OPERATION GROUPS
          </text>
          <text className={styles.svgColumnHead} x={X_ASSET - 7} y={16}>
            SENSITIVE ASSETS
          </text>
          {ordered.map(each => (
            <path key={each.id} className={styles.link({ isActive: each.isActive })} d={each.d} />
          ))}
          {run.entryPointOrder.map(id => {
            const ep = run.entryPoints[id]
            const y = entryY.get(id) ?? 0
            if (!ep) {
              return null
            }
            const kept = keptIn(run, entryPointCandidates(run, ep))
            return (
              <g key={id} data-key={`ep:${id}`} tabIndex={0} aria-label={ep.name}>
                <title>{`${ep.name} — ${ep.opIds.length} operations`}</title>
                <text
                  x={X_ENTRY - 12}
                  y={y + 4}
                  textAnchor="end"
                  className={`${styles.svgTextSmall} ${ep.mapping === 'empty' ? styles.svgTextMuted : ''}`}
                >
                  {truncate(ep.name, 24)}
                </text>
                <rect
                  className={styles.entryNode({ isActive: ep.active > 0 })}
                  x={X_ENTRY - 6}
                  y={y - 6}
                  width={12}
                  height={12}
                  rx={3}
                />
                {kept > 0 && (
                  <text className={`${styles.svgFindingCount} ${styles.svgHalo}`} x={X_ENTRY + 11} y={y + 4}>
                    ●{kept}
                  </text>
                )}
              </g>
            )
          })}
          {groups.map(key => {
            const group = run.groups[key]
            const y = groupY.get(key) ?? 0
            if (!group) {
              return null
            }
            const state = groupState(run, group)
            const kept = keptIn(run, groupCandidates(run, group))
            return (
              <g key={key} data-key={`grp:${key}`}>
                <title>{`${group.name} — ${group.opIds.length} operations`}</title>
                <rect className={styles.groupNode({ state })} x={X_GROUP - 5} y={y - 5} width={10} height={10} rx={2} />
                <text
                  x={X_GROUP + 11}
                  y={y + 4}
                  className={`${styles.svgText} ${styles.svgHalo} ${state === 'mapped' ? styles.svgTextMuted : ''}`}
                >
                  {truncate(groupLabel(run, group), 36)}
                </text>
                {kept > 0 && (
                  <text
                    className={`${styles.svgFindingCount} ${styles.svgHalo}`}
                    x={X_OUT - 4}
                    y={y + 4}
                    textAnchor="end"
                  >
                    ●{kept}
                  </text>
                )}
              </g>
            )
          })}
          {run.assetOrder.map(id => {
            const asset = run.assets[id]
            const y = assetY.get(id) ?? 0
            return asset ? (
              <g key={id} data-key={`asset:${id}`} tabIndex={0} aria-label={asset.name}>
                <title>{asset.name}</title>
                <circle className={styles.assetNode({ isHot: hotAssets.has(id) })} cx={X_ASSET} cy={y} r={7} />
                <text x={X_ASSET + 13} y={y + 4} className={styles.svgText}>
                  {truncate(asset.name, 24)}
                </text>
              </g>
            ) : null
          })}
          {groups.length === 0 && (
            <text x={X_GROUP} y={TOP + 40} className={`${styles.svgTextSmall} ${styles.svgTextMuted}`}>
              Mapping operations…
            </text>
          )}
        </svg>
      </div>
      <FindingsList run={run} />
    </div>
  )
}

// #endregion

// #region Findings list

const FINDING_ORDER: Record<CandidateState, number> = {
  kept: 0,
  suspected: 1,
  upheld: 1,
  refuted: 2,
  dropped: 2,
  merged: 3,
}

const FINDING_STATE_LABEL: Record<CandidateState, string> = {
  kept: 'verified',
  suspected: 'suspected',
  upheld: 'verifier upheld',
  refuted: 'verifier refuted',
  dropped: 'dropped in triage',
  merged: 'merged as duplicate',
}

export const FindingsList: React.FC<{ run: BeaconRunState }> = ({ run }) => {
  const candidates = run.candidateOrder
    .flatMap(uuid => run.candidates[uuid] ?? [])
    .sort((a, b) => FINDING_ORDER[a.state] - FINDING_ORDER[b.state])
    .slice(0, 80)
  return (
    <div className={styles.findingsList}>
      <h3 className={styles.findingsHeading}>
        <span>Findings</span>
        <span className={styles.findingsCount}>
          {keptTotal(run)} verified · {run.candidateOrder.length} seen
        </span>
      </h3>
      <ol className={styles.findings} aria-label="Candidates and findings">
        {candidates.length === 0 ? (
          <li className={styles.findingLocation}>Candidates appear here as investigations report them.</li>
        ) : (
          candidates.map(candidate => (
            <li
              key={candidate.finding_uuid}
              className={styles.findingItem({ state: candidate.state })}
              data-key={`cand:${candidate.finding_uuid}`}
              tabIndex={0}
            >
              <span>{truncate(candidate.weakness, 80)}</span>
              <span className={styles.findingLocation}>
                {candidate.file_path}:{candidate.line} · {FINDING_STATE_LABEL[candidate.state]}
              </span>
            </li>
          ))
        )}
      </ol>
    </div>
  )
}

// #endregion

// #region Lens radar

const RADAR_CENTER = 300
const RADAR_RADIUS = 250
const RADAR_INNER = 62

const radians = (degrees: number) => (degrees * Math.PI) / 180
const point = (radius: number, angle: number) =>
  `${RADAR_CENTER + radius * Math.cos(angle)},${RADAR_CENTER + radius * Math.sin(angle)}`

const sectorPath = (index: number) => {
  const a0 = radians(index * 60 - 90)
  const a1 = radians((index + 1) * 60 - 90)
  return `M${point(RADAR_INNER, a0)} L${point(RADAR_RADIUS, a0)} A${RADAR_RADIUS},${RADAR_RADIUS} 0 0 1 ${point(RADAR_RADIUS, a1)} L${point(RADAR_INNER, a1)} A${RADAR_INNER},${RADAR_INNER} 0 0 0 ${point(RADAR_INNER, a0)}Z`
}

const polar = (index: number, fraction: number, radius: number): [number, number] => {
  const angle = radians(index * 60 + 6 + fraction * 48 - 90)
  return [RADAR_CENTER + radius * Math.cos(angle), RADAR_CENTER + radius * Math.sin(angle)]
}

/** Spreads `count` blips over a sector in up to three bands so they never stack on one another. */
const blipPosition = (sectorIndex: number, index: number, count: number): [number, number] => {
  const perBand = Math.ceil(count / 3)
  const fraction = count > 1 ? (index % perBand) / Math.max(1, perBand - 1 || 1) : 0.5
  const radius = RADAR_INNER + 28 + (Math.floor(index / perBand) % 3) * 52 + (index % 2) * 14
  return polar(sectorIndex, Math.min(1, fraction), Math.min(radius, RADAR_RADIUS - 20))
}

const lensSectorState = (run: BeaconRunState, lens: (typeof DETECTION_LENSES)[number]) => {
  if (lens === 'ai_scan_surface_map') {
    return run.stages.surface_map.state
  }
  return run.lenses[lens].state
}

export const LensRadar: React.FC<{ run: BeaconRunState }> = ({ run }) => {
  const pill = pillFromState(run)
  const stageName = STAGES.find(({ stage }) => stage === currentStage(run))?.label ?? pill.label
  return (
    <div className={styles.radarWrap}>
      <svg
        viewBox="-70 -50 740 700"
        className={styles.radarSvg}
        role="img"
        aria-label={`Six detection lenses; ${pill.label}`}
      >
        <defs>
          <pattern id="beacon-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="8" height="8" className={styles.hatchFill} />
            <line x1="0" y1="0" x2="0" y2="8" className={styles.hatchLine} />
          </pattern>
        </defs>
        {DETECTION_LENSES.map((lens, index) => {
          const state = lensSectorState(run, lens)
          const [lx, ly] = polar(index, 0.5, RADAR_RADIUS + 40)
          const planned = run.lenses[lens].planned
          const done = run.investigationOrder.filter(
            id => run.investigations[id]?.lens === lens && run.investigations[id]?.state === 'done'
          ).length
          const sub =
            state === 'skipped'
              ? 'did not run'
              : lens === 'ai_scan_surface_map'
                ? `${Object.keys(run.operations).length} ops`
                : planned
                  ? `${done}/${planned}`
                  : ''
          const [sx, sy] = polar(index, 0.5, 150)
          return (
            <g key={lens}>
              <path className={styles.sector({ state })} d={sectorPath(index)} data-key={`lens:${lens}`}>
                <title>{`${lensLabel(lens)}: ${state}`}</title>
              </path>
              <text x={lx} y={ly} textAnchor="middle" className={styles.svgTextSmall} fontWeight={600}>
                {lensLabel(lens)}
              </text>
              <text x={lx} y={ly + 15} textAnchor="middle" className={`${styles.svgText} ${styles.svgTextMuted}`}>
                {sub}
              </text>
              {state === 'skipped' && (
                <text x={sx} y={sy} textAnchor="middle" className={`${styles.svgText} ${styles.svgTextMuted}`}>
                  <tspan x={sx}>{run.sinks?.error ? 'No sinks inventoried' : 'Nothing planned'}</tspan>
                  {run.sinks?.error && (
                    <tspan x={sx} dy={15}>
                      (sink inventory unavailable)
                    </tspan>
                  )}
                </text>
              )}
            </g>
          )
        })}
        {[120, 180].map(radius => (
          <circle key={radius} className={styles.ring} cx={RADAR_CENTER} cy={RADAR_CENTER} r={radius} />
        ))}
        {run.entryPointOrder.map((id, index) => {
          const ep = run.entryPoints[id]
          const [x, y] = blipPosition(0, index, run.entryPointOrder.length)
          const state = ep?.mapping === 'mapping' ? 'running' : ep?.mapping === 'pending' ? 'planned' : 'done'
          return (
            <circle key={id} className={styles.blip({ state })} cx={x} cy={y} r={5} data-key={`ep:${id}`}>
              <title>{`${ep?.name}: ${ep?.mapping}`}</title>
            </circle>
          )
        })}
        {DETECTION_LENSES.slice(1).flatMap((lens, lensIndex) => {
          const ids = run.investigationOrder.filter(id => run.investigations[id]?.lens === lens)
          return ids.map((id, index) => {
            const inv = run.investigations[id]
            const tone = inv ? candidateTone(run, inv.candidates) : undefined
            const state =
              inv?.state === 'running'
                ? 'running'
                : inv?.state === 'planned'
                  ? 'planned'
                  : tone === 'candidate' || tone === 'finding'
                    ? 'candidate'
                    : 'done'
            const [x, y] = blipPosition(lensIndex + 1, index, ids.length)
            return (
              <circle key={id} className={styles.blip({ state })} cx={x} cy={y} r={5} data-key={`inv:${id}`}>
                <title>{`${lensLabel(lens)} · ${inv?.label}: ${inv?.state}, ${inv?.candidates.length ?? 0} candidates`}</title>
              </circle>
            )
          })
        })}
        {DETECTION_LENSES.flatMap((lens, index) => {
          const candidates = run.candidateOrder.flatMap(uuid => {
            const candidate = run.candidates[uuid]
            return candidate?.lens === lens ? [candidate] : []
          })
          return candidates.map((candidate, candidateIndex) => {
            const [x, y] = polar(
              index,
              candidates.length > 1 ? candidateIndex / (candidates.length - 1) : 0.5,
              RADAR_RADIUS - 6
            )
            const tone =
              candidate.state === 'kept'
                ? 'kept'
                : candidate.state === 'suspected' || candidate.state === 'upheld'
                  ? 'candidate'
                  : 'gone'
            return (
              <circle
                key={candidate.finding_uuid}
                className={styles.tick({ tone })}
                cx={x}
                cy={y}
                r={candidate.state === 'kept' ? 4 : 3}
                data-key={`cand:${candidate.finding_uuid}`}
              >
                <title>{`${candidate.weakness} — ${candidate.state}`}</title>
              </circle>
            )
          })
        })}
        <circle className={styles.radarCenter} cx={RADAR_CENTER} cy={RADAR_CENTER} r={RADAR_INNER - 4} />
        <text className={styles.svgCenterTitle} x={RADAR_CENTER} y={RADAR_CENTER - 2} textAnchor="middle">
          {run.lensesDone}/6
        </text>
        <text
          x={RADAR_CENTER}
          y={RADAR_CENTER + 16}
          textAnchor="middle"
          className={`${styles.svgText} ${styles.svgTextMuted}`}
        >
          {run.status === 'completed' ? 'complete' : stageName.toLowerCase()}
        </text>
      </svg>
      <FindingsList run={run} />
    </div>
  )
}

// #endregion

// #region Inspector

const lensList = (lenses: readonly string[]) => lenses.map(lensLabel).join(', ') || 'none yet'

/** What the inspector says about the thing under the pointer or focus. */
export const describeFocus = ({
  run,
  key,
  dismissedById,
}: {
  run: BeaconRunState
  key: string
  dismissedById?: ReadonlyMap<string, DismissedLead>
}): React.ReactNode => {
  const [kind = '', ...rest] = key.split(':')
  const id = rest.join(':')
  switch (kind) {
    case 'ep': {
      const ep = run.entryPoints[id]
      return ep ? (
        <>
          <b>{ep.name}</b> · {ep.kind} · <span className={styles.mono}>{ep.location}</span> —{' '}
          {ep.mapping === 'empty'
            ? truncate(ep.reason, 220)
            : `${ep.opIds.length} operations; lenses run: ${lensList(ep.lenses)}; ${keptIn(run, entryPointCandidates(run, ep))} verified findings`}
        </>
      ) : undefined
    }
    case 'grp': {
      const group = run.groups[id]
      if (!group) {
        return undefined
      }
      const examined = group.opIds.filter(opId => run.operations[opId]?.isExamined).length
      const assets = group.assetIds.map(asset => run.assets[asset]?.name).filter(Boolean)
      return (
        <>
          <b>{group.name}</b> — {examined} of {group.opIds.length} operations examined; reaches{' '}
          {assets.join(', ') || 'no listed assets'}
        </>
      )
    }
    case 'op': {
      const operation = run.operations[id]
      return operation ? (
        <>
          <b>{operation.trigger}</b> → {operation.handler} · {operation.caller} caller · {operation.effect} ·{' '}
          <span className={styles.mono}>{operation.location}</span> — {operationState(run, operation)}
          {operation.candidates.length > 0 &&
            `; ${operation.candidates.map(uuid => run.candidates[uuid]?.weakness).join('; ')}`}
        </>
      ) : undefined
    }
    case 'asset': {
      const asset = run.assets[id]
      if (!asset) {
        return undefined
      }
      const examined = asset.opIds.filter(opId => run.operations[opId]?.isExamined).length
      return (
        <>
          <b>{asset.name}</b> ({asset.kind}) — {examined} of {asset.opIds.length} operations that reach it examined
        </>
      )
    }
    case 'ctl': {
      const control = run.controls[id]
      return control ? (
        <>
          <b>{control.name}</b> ({control.kind}) —{' '}
          {control.auditable ? control.state : 'Not checked: Beacon doesn’t audit this kind of control yet'};{' '}
          {keptIn(run, control.candidates)} verified findings
        </>
      ) : undefined
    }
    case 'inv': {
      const inv = run.investigations[id]
      return inv ? (
        <>
          <b>{inv.label}</b> · {lensLabel(inv.lens)} lens — {inv.state}
          {inv.state === 'done' &&
            `, read ${inv.files ?? 0} files, ${Math.round((inv.tokens ?? 0) / 1000)}k tokens, ${inv.candidates.length} candidates`}
        </>
      ) : undefined
    }
    case 'cand': {
      const candidate = run.candidates[id]
      const dismissed = dismissedById?.get(id)
      return candidate ? (
        <>
          <b>{candidate.title}</b> · {candidate.weakness} ·{' '}
          <span className={styles.mono}>
            {candidate.file_path}:{candidate.line}
          </span>{' '}
          — {candidate.state}
          {candidate.refutedOn.length > 0 && ` (refuted on ${candidate.refutedOn.join(', ')})`}
          {dismissed?.explanation && ` · ${truncate(dismissed.explanation.replace(/[`*]/g, ''), 220)}`}
        </>
      ) : undefined
    }
    case 'lens': {
      const lens = DETECTION_LENSES.find(each => each === id)
      if (!lens) {
        return undefined
      }
      const state = run.lenses[lens]
      return (
        <>
          <b>{lensLabel(lens)}</b> — {state.state}
          {state.reason && `: ${state.reason}`}
          {state.planned > 0 && `; ${state.planned} investigations planned`}
        </>
      )
    }
    case 'repo':
      return (
        <>
          <b>Repository-wide hygiene</b> — {run.repo.state}; {keptIn(run, run.repo.candidates)} verified findings
        </>
      )
    default:
      return undefined
  }
}

// #endregion
